import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { fetchPimActivations, fetchPimSummary } from "../api/client";
import { PimActivation, PimActivationSummary } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import StatCard from "../components/StatCard";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

function formatDuration(hours?: number): string {
  if (hours == null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)}h`;
}

// The collector writes exactly two statuses: "Active" while the elevation is
// still in force, "Completed" once it has expired or was recorded from the
// audit log after the fact. The chain used to test for approved/pending/
// expired/revoked instead, so every real row fell through to the default.
//
// Active is amber rather than green: it means somebody holds the role right
// now, which is the row to look at on this page, and green reads as "fine".
function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const cls =
    s === "active"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : s === "completed"
      ? "bg-gray-100 text-gray-500 border-gray-200"
      : "bg-blue-100 text-blue-700 border-blue-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
      {status}
    </span>
  );
}

export default function PimActivationsPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [activations, setActivations] = useState<PimActivation[]>([]);
  const [summary, setSummary] = useState<PimActivationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userUpn, setUserUpn] = useState("");
  const [roleName, setRoleName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Collect unique role names for dropdown
  const roleNames = Array.from(new Set(activations.map((a) => a.roleName).filter(Boolean))).sort();

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (userUpn) params.user_upn = userUpn;
    if (roleName) params.role_name = roleName;
    if (statusFilter) params.status = statusFilter;

    fetchPimActivations(params)
      .then((data) => setActivations(data))
      .catch((err) => setError(err.message ?? "Failed to load PIM activations."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, userUpn, roleName, statusFilter]);

  useEffect(() => {
    fetchData();
    fetchPimSummary()
      .then(setSummary)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Find most activated role
  const mostActivatedRole =
    summary && Object.keys(summary.byRole).length > 0
      ? Object.entries(summary.byRole).sort((a, b) => b[1] - a[1])[0][0]
      : "—";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PimActivation, any>[] = [
    {
      accessorKey: "activatedAt",
      header: "Date / Time",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "userUpn",
      header: "User",
      cell: ({ row, getValue }) => (
        <div>
          <div className="text-gray-800">{getValue()}</div>
          {row.original.userDisplayName && (
            <div className="text-xs text-gray-400">{row.original.userDisplayName}</div>
          )}
        </div>
      ),
    },
    { accessorKey: "roleName", header: "Role", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "durationHours",
      header: "Duration",
      cell: ({ getValue }) => formatDuration(getValue()),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue() || "—"} />,
    },
    {
      accessorKey: "justification",
      header: "Justification",
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined;
        if (!v) return <span className="text-gray-300">—</span>;
        return (
          <span title={v} className="max-w-xs truncate block text-gray-700 text-xs">
            {v}
          </span>
        );
      },
    },
    { accessorKey: "approvedBy", header: "Approved By", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      cell: ({ getValue }) => formatDate(getValue()),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PIM Activations</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Azure AD Privileged Identity Management — role activation history.
          </p>
        </div>
        <ExportButton
          endpoint="/api/export/pim-activations"
          params={{
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            user_upn: userUpn || undefined,
          }}
          filename="pim-activations.csv"
        />
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Activations" value={summary.totalActivations} severity="normal" />
          <StatCard label="Active Now" value={summary.activeNow} severity={summary.activeNow > 0 ? "warning" : "normal"} />
          <StatCard label="Distinct Users" value={summary.usersWithActivations} severity="normal" />
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Most-Activated Role</p>
            <p className="mt-1 text-sm font-semibold text-gray-800 truncate" title={mostActivatedRole}>
              {mostActivatedRole}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">User UPN</label>
          <input
            type="text"
            value={userUpn}
            onChange={(e) => setUserUpn(e.target.value)}
            placeholder="user@domain.com"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Role</label>
          <select
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-48"
          >
            <option value="">All roles</option>
            {roleNames.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-1.5 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors"
        >
          Apply
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading PIM activations…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && activations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
          <p className="text-lg font-semibold">No PIM activations found</p>
        </div>
      )}

      {!loading && !error && activations.length > 0 && (
        <DataTable data={activations} columns={columns} initialPageSize={25} />
      )}
    </div>
  );
}
